import {
    useState,
    useEffect,
    useContext,
    useMemo,
    useRef,
    useCallback,
} from "react";
import { Button, Alert } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import DeviceGraph from "./DeviceGraph";
import DeviceGraphView from "./DeviceGraphView";
import DeviceEditSidebar from "./DeviceEditSidebar";
import BindDeviceSidebar from "./BindDeviceSidebar";
import EntityOperationBar from "../../components/EntityOperationBar";
import {
    BsThreeDotsVertical,
    BsChevronDown,
    BsExclamationTriangleFill,
    BsStopwatch,
    BsGear,
} from "react-icons/bs";
import { AiOutlineWifi, AiOutlineClockCircle } from "react-icons/ai";
import { IoLocationSharp } from "react-icons/io5";
import { BiUnlink, BiPowerOff } from "react-icons/bi";
import { FaBatteryThreeQuarters } from "react-icons/fa";
import { MdSystemUpdateAlt } from "react-icons/md";
import { TbRestore } from "react-icons/tb";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import StarekContext from "../../context/StarekContext";
import { usePermissions } from "../../hooks/usePermissions";
import moment from "moment";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import { getHeartbeatStatus } from "../../utils";
import { deactivateData, restoreData } from "../../utils/softDelete";
import classNames from "classnames";
import DataTable from "react-data-table-component";
import DeviceCardMenu from "./DeviceCardMenu";
import ConfirmModal from "../../components/modals/ConfirmModal";
import ScheduleMaintenanceModal from "../../components/modals/ScheduleMaintenanceModal";
import { gql, useQuery, useMutation } from "@apollo/client";
import UpdateFirmwareModal from "./UpdateFirmwareModal";
import AddDeviceConfigWizard from "./AddDeviceConfigWizard";
import { Modal } from "react-bootstrap";
import {
    flattenManifestSignalsToTypeArray,
    flattenManifestSignalsToCosmetics,
    buildDeviceEffectiveConfiguration,
    fetchAvailableDeviceModels,
} from "../deviceProfile/AddDeviceProfileWizard";

// Dialog message constants
const DEVICE_MESSAGES = {
    MAINTENANCE_MARK: (
        deviceId,
    ) => `You are marking device ${deviceId} as under maintenance.

This device will be excluded from plant efficiency calculations until cleared.

Use this when the device requires immediate service or repairs.

Are you sure you wish to continue?`,

    MAINTENANCE_CLEAR: (
        deviceId,
    ) => `You are returning device ${deviceId} to active service by removing its maintenance status.

This device will resume counting towards your plant's operational efficiency metrics.

Are you sure you wish to continue?`,

    UNBIND_SINGLE: (deviceId) => `You are about to unbind device ${deviceId}.

Once unbound, this device will no longer be associated with this workspace.

This action cannot be easily undone. You will need to re-provision and re-bind the device to your workspace.

Are you sure you wish to continue?`,

    UNBIND_BULK: (count) => `You are about to unbind ${count} device(s).

Once unbound, these devices will no longer be associated with this workspace.

This action cannot be easily undone. You will need to re-provision and re-bind each device to your workspace.

Are you sure you wish to continue?`,

    REBOOT_SINGLE: (deviceId) => `You are about to reboot device ${deviceId}.

The device will temporarily go offline while rebooting and will automatically reconnect to the network.

No data will be lost. This is a safe operation.

Are you sure you wish to continue?`,

    REBOOT_BULK: (count) => `You are about to reboot ${count} device(s).

These devices will temporarily go offline while rebooting and will automatically reconnect to the network.

No data will be lost. This is a safe operation.

Are you sure you wish to continue?`,

    FACTORY_RESET_SINGLE: (
        deviceId,
    ) => `You are about to factory reset device ${deviceId}.

This will erase all settings and configurations on this device. Once done, the device will return to factory settings and become unprovisioned.

You will need to completely re-provision the device and re-bind it to your workspace.

Are you sure you wish to continue?`,

    FACTORY_RESET_BULK: (
        count,
    ) => `You are about to factory reset ${count} device(s).

This will erase all settings and configurations on these devices. Once done, they will return to factory settings and become unprovisioned.

You will need to completely re-provision each device and re-bind them to your workspace.

Are you sure you wish to continue?`,

    MAINTENANCE_BULK_ALREADY: (
        total,
        toCreate,
        already,
    ) => `You have selected ${total} devices. ${toCreate} will be marked as under maintenance, while ${already} are already under maintenance (no change will be made for those).

Maintenance devices are excluded from your plant's operational efficiency metrics and will not be assigned new jobs.

Are you sure you wish to continue?`,

    MAINTENANCE_BULK_NEW: (
        count,
    ) => `You are marking ${count} devices as under maintenance.

These devices will be excluded from your plant's operational efficiency calculations until their maintenance status is cleared.

These devices will not be assigned new jobs while under maintenance.

Are you sure you wish to continue?`,
};

// Kind of work being done. Mirrors TypesController.MAINTENANCE_TYPES on the
// backend; used as a fallback when /_types hasn't been read yet. Recorded on
// the ADHOC schedule so maintenance reports can be sliced by type.
const MAINTENANCE_TYPE_FALLBACK = {
    PREVENTIVE: 1,
    CORRECTIVE: 2,
    PREDICTIVE: 3,
    BREAKDOWN: 4,
};

const MAINTENANCE_TYPE_LABELS = {
    PREVENTIVE: "Preventive",
    CORRECTIVE: "Corrective",
    PREDICTIVE: "Predictive",
    BREAKDOWN: "Breakdown",
};

// Fallback map center (India) used only when a device has no gps_lat/gps_lon
// attribute set yet, so the picker still has something sensible to show.
const DEFAULT_MAP_CENTER = { lat: 20.5937, lng: 78.9629 };
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const extractLatLng = (latLng) => {
    if (!latLng) return null;
    const lat = typeof latLng.lat === "function" ? latLng.lat() : latLng.lat;
    const lng = typeof latLng.lng === "function" ? latLng.lng() : latLng.lng;
    return { lat, lng };
};

const LocationPopup = ({ locationData, onClose, onSave, canEdit }) => {
    const [geographicalName, setGeographicalName] = useState("");
    const [loading, setLoading] = useState(true);
    const [lat, setLat] = useState(locationData.location?.latitude ?? "");
    const [lon, setLon] = useState(locationData.location?.longitude ?? "");
    const [saving, setSaving] = useState(false);

    const hasLocation = lat !== "" && lon !== "" && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lon));

    useEffect(() => {
        if (!hasLocation) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
                );
                const data = await response.json();
                setGeographicalName(data.display_name || "Unknown Location");
            } catch (error) {
                setGeographicalName("Unknown Location");
            } finally {
                setLoading(false);
            }
            // eslint-disable-next-line no-magic-numbers
        }, 500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lat, lon]);

    const handlePositionChange = (latLng) => {
        const next = extractLatLng(latLng);
        if (!next) return;
        setLat(next.lat.toFixed(6));
        setLon(next.lng.toFixed(6));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(locationData.deviceId, Number(lat), Number(lon));
        } finally {
            setSaving(false);
        }
    };

    const markerPosition = hasLocation
        ? { lat: Number(lat), lng: Number(lon) }
        : DEFAULT_MAP_CENTER;

    return (
        <div className="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-gray-800 tw-bg-opacity-50 tw-flex tw-justify-center tw-items-center">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-4 tw-w-[40vw]">
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                    <h3 className="tw-text-lg tw-font-bold">Location</h3>
                    <button
                        className="tw-text-gray-500 tw-font-bold tw-text-xl"
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className="tw-flex tw-max-w-[38vw] tw-justify-between">
                    <div className="tw-max-w-[15vw]">
                        <p className="tw-text-sm tw-text-gray-600">
                            <strong>Geographical:</strong>{" "}
                            {!hasLocation
                                ? "Not set"
                                : loading
                                    ? "Loading..."
                                    : geographicalName}
                        </p>
                        {locationData?.plants?.length > 0 && (
                            <div className="tw-text-sm tw-text-gray-600">
                                <strong>At Plant:</strong>
                                {locationData.plants.map((plant, index) => (
                                    <p className="tw-mt-0 tw-mb-0" key={index}>
                                        - {plant}
                                    </p>
                                ))}
                            </div>
                        )}
                        {canEdit && (
                            <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-1">
                                <label className="tw-text-xs tw-text-gray-500">
                                    Latitude
                                    <input
                                        type="number"
                                        step="any"
                                        className="tw-block tw-w-full tw-border tw-rounded tw-px-1 tw-py-0.5 tw-text-sm"
                                        value={lat}
                                        onChange={(e) => setLat(e.target.value)}
                                    />
                                </label>
                                <label className="tw-text-xs tw-text-gray-500">
                                    Longitude
                                    <input
                                        type="number"
                                        step="any"
                                        className="tw-block tw-w-full tw-border tw-rounded tw-px-1 tw-py-0.5 tw-text-sm"
                                        value={lon}
                                        onChange={(e) => setLon(e.target.value)}
                                    />
                                </label>
                                <button
                                    className="tw-mt-1 tw-bg-blue-500 hover:tw-bg-blue-600 tw-text-white tw-text-sm tw-rounded tw-px-2 tw-py-1 disabled:tw-opacity-50"
                                    disabled={!hasLocation || saving}
                                    onClick={handleSave}
                                >
                                    {saving ? "Saving..." : "Save Location"}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Render Map */}
                    {GOOGLE_MAPS_API_KEY ? (
                        <div className="tw-w-[20vw] tw-h-[200px] tw-rounded-lg tw-overflow-hidden">
                            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                                <Map
                                    defaultCenter={markerPosition}
                                    defaultZoom={13}
                                    gestureHandling="greedy"
                                    disableDefaultUI
                                    className="tw-h-[200px] tw-w-full"
                                    onClick={
                                        canEdit
                                            ? (e) =>
                                                handlePositionChange(
                                                    e.detail.latLng,
                                                )
                                            : undefined
                                    }
                                >
                                    <Marker
                                        position={markerPosition}
                                        draggable={canEdit}
                                        onDragEnd={(e) =>
                                            handlePositionChange(e.latLng)
                                        }
                                    />
                                </Map>
                            </APIProvider>
                            {!hasLocation && (
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    {canEdit
                                        ? "No location set — click the map or drag the pin."
                                        : "No location set."}
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="tw-text-sm tw-text-red-500">
                            Google Maps API key is not configured
                            (REACT_APP_GOOGLE_MAPS_API_KEY).
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

// Same 0-1023 raw-count-to-percentage convention every analog signal in this
// codebase already uses (see DeviceGraph.jsx) - firmware pre-scales into that
// range, so no separate min/max metadata is needed here.
const signalHistoryChartMargin = { top: 10, right: 10, left: 10, bottom: 30 };

const SignalHistoryPopup = ({ title, points, ipAddress, onClose }) => {
    const startTime = moment().subtract(1, "hour");
    const endTime = moment();

    const graphData = (points || []).map((point) => ({
        time: point.time,
        value: (point.measure_value / 1023) * 100,
        measure_value: point.measure_value,
    }));

    // points is sorted ascending by time (see mount site), so the last
    // element is the most recent reading.
    const latestPercent =
        graphData.length > 0
            ? graphData[graphData.length - 1].value
            : null;
    const maxPercent =
        graphData.length > 0
            ? Math.max(...graphData.map((d) => d.value))
            : null;
    const minPercent =
        graphData.length > 0
            ? Math.min(...graphData.map((d) => d.value))
            : null;

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload?.length) {
            const dataPoint = payload[0].payload;
            return (
                <div className="tw-bg-white tw-p-3 tw-border tw-rounded tw-shadow-lg">
                    <p className="tw-text-sm tw-font-medium">
                        {`Value: ${payload[0].value.toFixed(1)}%`}
                    </p>
                    <p className="tw-text-xs tw-text-gray-600">
                        {`Time: ${moment
                            .unix(dataPoint.time)
                            .local()
                            .format("HH:mm:ss")}`}
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-gray-800 tw-bg-opacity-50 tw-flex tw-justify-center tw-items-center">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-4 tw-w-[40vw]">
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                    <h3 className="tw-text-lg tw-font-bold">{title}</h3>
                    <button
                        className="tw-text-gray-500 tw-font-bold tw-text-xl"
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className="tw-flex tw-max-w-[38vw] tw-justify-between">
                    <div className="tw-w-[15vw] tw-h-[200px] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-3">
                        <span className="tw-text-5xl tw-font-bold tw-text-gray-800">
                            {latestPercent !== null
                                ? `${latestPercent.toFixed(0)}%`
                                : "—"}
                        </span>
                        {maxPercent !== null && (
                            <p className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-mb-2">
                                {`Max: ${maxPercent.toFixed(0)}% · Min: ${minPercent.toFixed(0)}%`}
                            </p>
                        )}
                        {ipAddress && (
                            <p className="tw-text-sm tw-text-gray-600">
                                <strong>IP:</strong> {ipAddress}
                            </p>
                        )}
                    </div>

                    <div className="tw-w-[20vw] tw-h-[200px] tw-rounded-lg tw-overflow-hidden">
                        {graphData.length === 0 ? (
                            <div className="tw-h-full tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-text-sm">
                                Couldn't find any data
                            </div>
                        ) : (
                            <DeviceGraphView
                                graphData={graphData}
                                signalColor="#0080ff"
                                lineType="linear"
                                isDigital={false}
                                isExpanded={false}
                                startTime={startTime}
                                endTime={endTime}
                                chartMargin={signalHistoryChartMargin}
                                CustomTooltip={CustomTooltip}
                                tickFormatter={(unixTime) =>
                                    moment.unix(unixTime).format("HH:mm")
                                }
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// signalHistory arrays aren't guaranteed chronological (Timestream array_agg
// has no inner ORDER BY), so "latest" must be found explicitly, not assumed
// to be the last element.
function getLatestPoint(points) {
    if (!points?.length) return null;
    return points.reduce((latest, p) => (p.time > latest.time ? p : latest));
}

export const Devices = () => {
    const starekContext = useContext(StarekContext);
    const { can } = usePermissions();
    const canWrite = can("device", "WRITE");
    const canDestroy = can("device", "DESTROY");
    const canReboot = can("device_reboot", "EXECUTE");
    const canUpdateFirmware = can("device_update_firmware", "EXECUTE");

    // Todo: Add refetch of device after successful call of each mutation
    //Todo: Show earliest maintenance first for scheduled maintenance in the maintenance column
    //Todo: There will be only one upcoming maintenance for each device. So, show that only based upon schedule type as 1. So with every new "upcoming maintenance" creation, the previous one will be deleted automatically. You can use schedule_type for checking as it will be one for ADHOC
    //Todo: Toggle in action menu between under maintenance and clear maintenance. Once marked as under maintenance, the action menu should show clear maintenance option. Clear maintenance will remove the device from under maintenance list by deleting ad-hoc maintenance schedule from database
    //Todo: Show list of firmware versions available in the UpdateFirmwareModal and show current version as well by comparing with device.firmware_version. When clicked on any firmware and update then show confirmation modal and based upon confirmation call update firmware gql with required payload to update the firmware of the device
    const GET_DEVICES_IN_PLANT = gql`
        query GetDevicesForPlant($where: ShadowDeviceWhereInput) {
            shadowDevices(where: $where) {
                id
                name
                device
                is_active
                deleted_at
                parameters
                device_profile {
                    id
                    name
                }
                maintenance_schedules {
                    schedule_type
                    schedule_attributes
                    description
                }
                jobSchedules {
                    completed_cycles
                }
                installed_at_zones {
                    id
                    name
                }
            }
        }
    `;

    // Installations - which zone(s) this device is attached to
    // (ShadowDevice.installed_at_zones, an m:n Prisma relation with Zone -
    // see the device sidebar's Installations tab). Zone list is scoped to
    // the current plant, same as the Zones page's own query.
    const GET_ZONES_FOR_INSTALLATIONS = gql`
        query GetZonesForInstallations($plant_id: Int!) {
            zones(where: { plant_id: { equals: $plant_id } }) {
                id
                name
            }
        }
    `;

    const UPDATE_DEVICE_ZONES = gql`
        mutation UpdateDeviceZones(
            $id: String!
            $zoneIds: [ZoneWhereUniqueInput!]!
        ) {
            updateOneShadowDevice(
                where: { id: $id }
                data: { installed_at_zones: { set: $zoneIds } }
            ) {
                id
            }
        }
    `;

    const GET_DEVICE_PROFILES_FOR_ASSIGN = gql`
        query GetDeviceProfilesForAssign($plant_id: Int!) {
            deviceProfiles(where: { plant_id: { equals: $plant_id } }) {
                id
                name
                configuration
            }
        }
    `;

    const UPDATE_MANY_SHADOW_DEVICE = gql`
        mutation UpdateManyShadowDevice($data: ShadowDeviceUpdateManyMutationInput!, $where: ShadowDeviceWhereInput) {
            updateManyShadowDevice(data: $data, where: $where) {
                count
            }
        }
    `;

    const CREATE_MAINTENANCE_SCHEDULES = gql`
        mutation CreateManyMaintenanceSchedule(
            $data: [MaintenanceScheduleCreateManyInput!]!
        ) {
            createManyMaintenanceSchedule(data: $data) {
                count
            }
        }
    `;

    // Plant-scoped downtime reasons (Reporting Phase 1). Same catalogue the
    // reports page filters by, so a reason picked here lines up with the
    // Downtime Reason report.
    const GET_DOWNTIME_REASONS = gql`
        query GetDowntimeReasonsForPlant($plantId: Int!) {
            downtimeReasons(
                where: {
                    plant_id: { equals: $plantId }
                    is_active: { equals: true }
                }
            ) {
                id
                name
                is_planned
            }
        }
    `;

    const DEVICE_UNBIND = gql`
        mutation DeviceUnbind($deviceId: String!) {
            deviceUnbind(device_id: $deviceId)
        }
    `;

    const DEVICE_COMMAND = gql`
        mutation DeviceCommand(
            $command: String!
            $deviceId: String!
            $parameters: JSONObject
        ) {
            deviceCommand(
                command: $command
                device_id: $deviceId
                parameters: $parameters
            )
        }
    `;

    const DELETE_MAINTENANCE_SCHEDULES = gql`
        mutation deleteManyMaintenanceSchedule(
            $where: MaintenanceScheduleWhereInput!
        ) {
            deleteManyMaintenanceSchedule(where: $where) {
                count
            }
        }
    `;

    const DEVICE_SET_ATTRIBUTE = gql`
        mutation DeviceSetAttribute(
            $deviceId: String!
            $name: String!
            $value: JSON
        ) {
            deviceSetAttribute(device_id: $deviceId, name: $name, value: $value)
        }
    `;

    const UPDATE_DEVICE_PARAMETERS = gql`
        mutation UpdateDeviceParameters(
            $where: ShadowDeviceWhereUniqueInput!
            $data: ShadowDeviceUpdateInput!
        ) {
            updateOneShadowDevice(where: $where, data: $data) {
                id
                parameters
            }
        }
    `;

    const CREATE_ONE_DEVICE_PROFILE = gql`
        mutation CreateOneDeviceProfile($data: DeviceProfileCreateInput!) {
            createOneDeviceProfile(data: $data) {
                id
                name
                description
                configuration
            }
        }
    `;
    const [searchParams, setSearchParams] = useSearchParams();

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const setShowInactive = (value, options = {}) => {
        const next = new URLSearchParams(searchParams);
        if (value) {
            next.set("showInactive", "1");
        } else {
            next.delete("showInactive");
        }
        setSearchParams(next, options);
    };

    const ASSIGN_DEVICE_PROFILE = gql`
        mutation AssignDeviceProfile(
            $deviceId: String!
            $deviceProfileId: Float!
            $rebootNow: Boolean
        ) {
            assignDeviceProfile(
                device_id: $deviceId
                device_profile_id: $deviceProfileId
                reboot_now: $rebootNow
            )
        }
    `;

    const UNASSIGN_DEVICE_PROFILE = gql`
        mutation UnassignDeviceProfile($deviceId: String!, $rebootNow: Boolean) {
            unassignDeviceProfile(device_id: $deviceId, reboot_now: $rebootNow)
        }
    `;

    const UPDATE_DEVICE_SIGNAL_TYPE = gql`
        mutation UpdateDeviceSignalType(
            $deviceId: String!
            $name: String!
            $type: Float!
            $rebootNow: Boolean
        ) {
            updateDeviceSignalType(
                device_id: $deviceId
                name: $name
                type: $type
                reboot_now: $rebootNow
            )
        }
    `;

    const UPDATE_DEVICE_SIGNALS = gql`
        mutation UpdateDeviceSignals(
            $deviceId: String!
            $signals: [JSONObject!]!
            $rebootNow: Boolean
        ) {
            updateDeviceSignals(
                device_id: $deviceId
                signals: $signals
                reboot_now: $rebootNow
            )
        }
    `;

    const UPDATE_DEVICE_INTERPRETATIONS = gql`
        mutation UpdateDeviceInterpretations(
            $deviceId: String!
            $interpretations: [JSONObject!]!
            $rebootNow: Boolean
        ) {
            updateDeviceInterpretations(
                device_id: $deviceId
                interpretations: $interpretations
                reboot_now: $rebootNow
            )
        }
    `;

    const {
        data: deviceData,
        loading: deviceLoading,
        error: deviceError,
        refetch,
        startPolling,
        stopPolling,
    } = useQuery(GET_DEVICES_IN_PLANT, {
        variables: {
            where: {
                plant: {
                    is: {
                        id: {
                            equals: starekContext.lastUsedPlant?.id,
                        },
                    },
                },
                ...(showInactive ? {} : { is_active: { equals: true } }),
            },
        },
        // Don't use pollInterval here, we'll control it manually
    });

    const fetchDevices = async () => {
        try {
            await refetch();
        } catch (error) {
            console.error("Error refetching devices");
        }
    };

    const shadowDevices = useMemo(() => {
        return deviceData?.shadowDevices ?? [];
    }, [deviceData?.shadowDevices]);
    // const [searchTerm, setSearchTerm] = useState("");
    const [selectedLocation, setSelectedLocation] = useState(null);
    // { [deviceId]: { "/virt/wifi_rssi": [{time, measure_value}], "/virt/pwr": [...] } }
    const [signalHistory, setSignalHistory] = useState({});
    const [selectedSignalDialog, setSelectedSignalDialog] = useState(null); // { deviceId, signalKey, title }
    // Which device's LiveView modal (DeviceGraph, large) is currently open -
    // replaces the old per-row expandable-graph state now that the grid no
    // longer expands inline; DeviceGraph itself is unmounted/remounted with
    // the modal, same lifecycle the expand-row used to give it.
    const [liveViewDevice, setLiveViewDevice] = useState(null);
    const [editSidebarOpen, setEditSidebarOpen] = useState(false);
    const [editSidebarDevice, setEditSidebarDevice] = useState(null);
    // Which tab the sidebar should land on when it opens - lets LiveView's
    // "+ Create interpretation" banner jump straight to the Interpretation
    // tab instead of the default Signals tab.
    const [editSidebarInitialTab, setEditSidebarInitialTab] = useState("signals");
    const editSidebarDeviceLive = useMemo(() => {
        if (!editSidebarDevice) return null;
        return (
            shadowDevices.find((d) => d.id === editSidebarDevice.id) ||
            editSidebarDevice
        );
    }, [shadowDevices, editSidebarDevice]);
    const { data: deviceProfilesForAssignData } = useQuery(
        GET_DEVICE_PROFILES_FOR_ASSIGN,
        {
            variables: { plant_id: starekContext?.lastUsedPlant?.id },
            skip: !starekContext?.lastUsedPlant?.id,
            client: starekContext.engineClient,
            // Fetch fresh every time this page mounts (not on every sidebar
            // open - just entering the page) rather than serving Apollo's
            // cached list, which could include a profile deleted from
            // elsewhere in the same session.
            fetchPolicy: "network-only",
        },
    );
    const deviceProfilesForAssign =
        deviceProfilesForAssignData?.deviceProfiles ?? [];
    const { data: zonesForInstallationsData } = useQuery(
        GET_ZONES_FOR_INSTALLATIONS,
        {
            variables: { plant_id: starekContext?.lastUsedPlant?.id },
            skip: !starekContext?.lastUsedPlant?.id,
            client: starekContext.engineClient,
            fetchPolicy: "network-only",
        },
    );
    const zonesForInstallations = zonesForInstallationsData?.zones ?? [];
    const [updateDeviceZones] = useMutation(UPDATE_DEVICE_ZONES, {
        client: starekContext.engineClient,
    });
    const [openBindSidebar, setOpenBindSidebar] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectionTick, setClearSelectionTick] = useState(false);
    const [menuOpenForId, setMenuOpenForId] = useState(null);
    const [menuAnchorRect, setMenuAnchorRect] = useState(null);
    const [underMaintenance, setUnderMaintenance] = useState(() => new Set());
    const [fwModal, setFwModal] = useState({ open: false, device: null });
    const [pendingFw, setPendingFw] = useState(null);
    const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
    const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [scheduleCfg, setScheduleCfg] = useState({
        open: false,
        targetIds: [],
    });
    const [configWizard, setConfigWizard] = useState({ open: false, device: null });

    // Use ref to track the open LiveView device for mutation callback to
    // avoid closure issues
    const liveViewDeviceRef = useRef(liveViewDevice);
    useEffect(() => {
        liveViewDeviceRef.current = liveViewDevice;
    }, [liveViewDevice]);

    // Batched last-1-hour /virt/wifi_rssi + /virt/pwr history for every listed device.
    // Drives both the Status column icon coloring and the history dialogs below -
    // the dialogs reuse this fetch rather than issuing their own.
    const { scheme, host, port } = starekContext.lastUsedWorkspace?.engine || {};
    const datapointsWorkspace = starekContext.lastUsedWorkspace?.name;
    const datapointsAuthToken = starekContext.auth;
    const datapointsUrl = `${scheme}://${host}:${port}/api/v1/${datapointsWorkspace}/datapoints`;

    const fetchSignalHistory = useCallback(async () => {
        const deviceIds = shadowDevices.map((d) => d.id);
        if (deviceIds.length === 0) return;
        const nowUtc = moment().utc();
        const startTimeUtc = nowUtc.clone().subtract(1, "hour");
        try {
            const response = await fetch(datapointsUrl, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${datapointsAuthToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    devices: deviceIds,
                    start_time: startTimeUtc.unix(),
                    end_time: nowUtc.unix(),
                    group_by_device: true,
                    group_by_signal: true,
                }),
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const jsonData = await response.json();
            setSignalHistory(jsonData?.data ?? {});
        } catch (error) {
            console.error("Failed to fetch signal history:", error);
        }
    }, [datapointsUrl, datapointsAuthToken, shadowDevices]);

    useEffect(() => {
        fetchSignalHistory();
        const interval = setInterval(fetchSignalHistory, 60000);
        return () => clearInterval(interval);
    }, [fetchSignalHistory]);

    const [createManyMaintenance, { loading: creatingMaint }] = useMutation(
        CREATE_MAINTENANCE_SCHEDULES,
        { onCompleted: () => fetchDevices() },
    );

    const [deviceUnbindMutation] = useMutation(DEVICE_UNBIND, {
        onCompleted: () => fetchDevices(),
    });
    const [deviceCommandMutation] = useMutation(DEVICE_COMMAND, {
        onCompleted: () => {
            // Only refresh device list if the LiveView modal isn't open -
            // use ref to avoid stale closure
            if (!liveViewDeviceRef.current) {
                fetchDevices();
            }
        },
    });
    const [deleteManyMaintenance] = useMutation(DELETE_MAINTENANCE_SCHEDULES, {
        onCompleted: () => fetchDevices(),
    });
    const [updateDeviceParameters] = useMutation(UPDATE_DEVICE_PARAMETERS, {
        onCompleted: () => fetchDevices(),
    });
    const [createDeviceProfile] = useMutation(CREATE_ONE_DEVICE_PROFILE);
    const [assignDeviceProfile] = useMutation(ASSIGN_DEVICE_PROFILE, {
        onCompleted: () => fetchDevices(),
    });
    const [unassignDeviceProfile] = useMutation(UNASSIGN_DEVICE_PROFILE, {
        onCompleted: () => fetchDevices(),
    });
    const [updateDeviceSignalType] = useMutation(UPDATE_DEVICE_SIGNAL_TYPE);
    const [updateDeviceSignals] = useMutation(UPDATE_DEVICE_SIGNALS);
    const [updateDeviceInterpretations] = useMutation(
        UPDATE_DEVICE_INTERPRETATIONS,
    );
    const [updateManyShadowDevice] = useMutation(UPDATE_MANY_SHADOW_DEVICE, {
        client: starekContext.engineClient,
    });
    const [deviceSetAttribute] = useMutation(DEVICE_SET_ATTRIBUTE, {
        client: starekContext.engineClient,
    });

    const hasSelection = selectedRows.length > 1;

    // Why a machine is going under maintenance. All three are required, and
    // recorded on the record so the reason survives beyond the person who typed
    // it. The two coded fields (type + downtime reason) are what the reports
    // aggregate on; the comment carries the detail a code can't.
    const [maintenanceComment, setMaintenanceComment] = useState("");
    const [maintenanceType, setMaintenanceType] = useState("");
    const [maintenanceReasonId, setMaintenanceReasonId] = useState("");

    const plantIdForReasons = Number(starekContext?.lastUsedPlant?.id) || null;
    const { data: downtimeReasonsData } = useQuery(GET_DOWNTIME_REASONS, {
        variables: { plantId: plantIdForReasons },
        skip: !plantIdForReasons,
    });
    const downtimeReasons = downtimeReasonsData?.downtimeReasons ?? [];

    // Codes come from /_types when available so the UI can't drift from the API.
    const MAINTENANCE_TYPES =
        starekContext?.types?.maintenance_schedules?.maintenance_types ??
        MAINTENANCE_TYPE_FALLBACK;
    const maintenanceTypeOptions = Object.entries(MAINTENANCE_TYPES).map(
        ([key, value]) => ({
            key,
            value,
            label: MAINTENANCE_TYPE_LABELS[key] ?? key,
        }),
    );

    const resetMaintenanceInputs = () => {
        setMaintenanceComment("");
        setMaintenanceType("");
        setMaintenanceReasonId("");
    };

    const openConfirm = (cfg) => {
        resetMaintenanceInputs();
        setConfirmCfg({ open: true, ...cfg });
    };
    const closeConfirm = () => {
        resetMaintenanceInputs();
        setConfirmCfg({ open: false });
    };
    const openSchedule = (ids) =>
        setScheduleCfg({ open: true, targetIds: ids });
    const closeSchedule = () => setScheduleCfg({ open: false, targetIds: [] });
    const TYPE_ADHOC =
        starekContext?.types?.maintenance_schedules?.schedule_types?.ADHOC ?? 1;
    const TYPE_SCHEDULED =
        starekContext?.types?.maintenance_schedules?.schedule_types
            ?.SCHEDULED ?? 2;

    // Control device list polling based on whether the LiveView modal is open
    useEffect(() => {
        if (liveViewDevice) {
            // Stop polling while LiveView is open
            stopPolling();
        } else {
            // Start polling every 60 seconds otherwise
            startPolling(60000);
        }

        return () => {
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveViewDevice]); // Only depend on liveViewDevice, not the functions

    useEffect(() => {
        const devices = deviceData?.shadowDevices ?? [];
        const next = new Set();
        devices.forEach((d) => {
            if (Array.isArray(d?.maintenance_schedules)) {
                if (
                    d.maintenance_schedules.some(
                        (m) => m?.schedule_type === TYPE_ADHOC,
                    )
                ) {
                    next.add(d.id);
                }
            }
        });

        // Only update if the set actually changed to prevent infinite loops
        const currentIds = Array.from(underMaintenance).sort().join(",");
        const nextIds = Array.from(next).sort().join(",");
        if (currentIds !== nextIds) {
            setUnderMaintenance(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceData, TYPE_ADHOC]); // Use deviceData directly which is stable from Apollo

    useEffect(() => {
        if (selectedRows.length > 1) {
            setMenuOpenForId(null);
        }
    }, [selectedRows]);

    const runCreateManyInChunks = async (rows, chunkSize = 50) => {
        const chunks = [];
        for (let i = 0; i < rows.length; i += chunkSize) {
            chunks.push(rows.slice(i, i + chunkSize));
        }
        await Promise.all(
            chunks.map((data) =>
                createManyMaintenance({ variables: { data } }),
            ),
        );
    };

    const unbindDevice = async ({ deviceId }) => {
        await deviceUnbindMutation({ variables: { deviceId } });
        return true;
    };

    const getEarliestScheduled = (row, TYPE_SCHEDULED) => {
        const list = Array.isArray(row?.maintenance_schedules)
            ? row.maintenance_schedules
            : [];
        const now = Date.now();
        const scheduled = list.filter(
            (m) => m?.schedule_type === TYPE_SCHEDULED,
        );
        if (scheduled.length === 0) return null;

        const withFrom = scheduled
            .map((m) => ({
                ...m,
                __from: new Date(m?.schedule_attributes?.from || 0).getTime(),
            }))
            .filter((m) => Number.isFinite(m.__from) && m.__from > now);
        return withFrom.length > 0
            ? withFrom.sort((a, b) => a.__from - b.__from)[0]
            : null;
    };

    const getMaintenanceStatus = (row, TYPE_ADHOC, TYPE_SCHEDULED) => {
        const list = Array.isArray(row?.maintenance_schedules)
            ? row.maintenance_schedules
            : [];

        const adhoc = list.find((m) => m?.schedule_type === TYPE_ADHOC);
        if (adhoc) {
            return {
                type: "adhoc",
                data: adhoc,
                display: "Under Maintenance",
            };
        }

        const upcoming = getEarliestScheduled(row, TYPE_SCHEDULED);
        if (upcoming) {
            const fromISO = upcoming?.schedule_attributes?.from;
            const from = fromISO ? moment.utc(fromISO) : null;
            const diffMs = from ? from.diff(moment.utc()) : null;
            const human =
                diffMs != null
                    ? diffMs > 0
                        ? `in ${moment.duration(diffMs).humanize()}`
                        : `${moment.duration(-diffMs).humanize()} ago`
                    : "";
            return {
                type: "scheduled",
                data: upcoming,
                display: `Next Due: ${from ? from.format("MMM DD, YYYY") : "Not scheduled"
                    }`,
                humanized: human,
            };
        }

        // Fallback: No maintenance
        return {
            type: "none",
            display: "All Good",
        };
    };

    const sendDeviceCommand = async ({
        deviceId,
        command,
        parameters = {},
    }) => {
        await deviceCommandMutation({
            variables: { command, deviceId, parameters },
        });
        return true;
    };
    const openFirmwareModal = (deviceRow) =>
        setFwModal({ open: true, device: deviceRow });

    const doUnderMaintenance = async (deviceIds, details = {}) => {
        const {
            comment = "",
            maintenanceType: maintType = null,
            downtimeReasonId = null,
        } = details;
        const reason = comment;
        const downtimeReason = downtimeReasons.find(
            (r) => r.id === downtimeReasonId,
        );
        const maintTypeKey =
            Object.keys(MAINTENANCE_TYPES).find(
                (k) => MAINTENANCE_TYPES[k] === maintType,
            ) ?? null;
        const plantId = starekContext?.lastUsedPlant?.id ?? null;
        const user_Id = Number(starekContext?.user?.id);
        if (!user_Id) {
            throw new Error(
                "No logged-in user id (created_by_id/updated_by_id required).",
            );
        }

        const already = deviceIds.filter((id) => underMaintenance.has(id));
        const toCreate = deviceIds.filter((id) => !underMaintenance.has(id));

        if (toCreate.length === 0) {
            starekContext.showSuccess?.(
                `${already.length} device(s) already Under Maintenance. Nothing to change.`,
            );
            return;
        }
        await deleteManyMaintenance({
            variables: {
                where: {
                    device_id: { in: toCreate },
                    schedule_type: { equals: TYPE_ADHOC },
                },
            },
        });

        const nowISO = new Date().toISOString();
        const rows = toCreate.map((id) => ({
            name: `Adhoc Maintenance - ${id} - ${nowISO}`,
            description: reason,
            plant_id: plantId,
            schedule_type: TYPE_ADHOC,
            schedule_attributes: {
                from: nowISO,
                reason,
                // Coded fields the maintenance/downtime reports group by.
                maintenance_type: maintType,
                maintenance_type_key: maintTypeKey,
                maintenance_type_name: maintTypeKey
                    ? MAINTENANCE_TYPE_LABELS[maintTypeKey] ?? maintTypeKey
                    : null,
                downtime_reason_id: downtimeReasonId,
                // Name is denormalised so a report still reads correctly if the
                // reason is later renamed or deactivated.
                downtime_reason_name: downtimeReason?.name ?? null,
                downtime_reason_is_planned: downtimeReason?.is_planned ?? null,
            },
            device_id: id,
            created_by_id: user_Id,
            updated_by_id: user_Id,
        }));

        try {
            await runCreateManyInChunks(rows);
            await Promise.allSettled(
                toCreate.map((id) =>
                    deviceSetAttribute({
                        variables: { deviceId: id, name: "under_maintenance", value: true },
                    }),
                ),
            );
            setUnderMaintenance((prev) => {
                const next = new Set(prev);
                toCreate.forEach((id) => next.add(id));
                return next;
            });
            starekContext.showSuccess?.(
                `Marked ${toCreate.length} device(s) Under Maintenance. ` +
                (already.length
                    ? `${already.length} already Under Maintenance (left unchanged).`
                    : ""),
            );
        } catch (e) {
            starekContext.showError?.(
                e.message || "Failed to mark Under Maintenance",
            );
        }
    };

    const doClearUnderMaintenance = async (deviceIds) => {
        try {
            await deleteManyMaintenance({
                variables: {
                    where: {
                        device_id: { in: deviceIds },
                        schedule_type: { equals: TYPE_ADHOC },
                    },
                },
            });
            await Promise.allSettled(
                deviceIds.map((id) =>
                    deviceSetAttribute({
                        variables: { deviceId: id, name: "under_maintenance", value: false },
                    }),
                ),
            );
            setUnderMaintenance((prev) => {
                const next = new Set(prev);
                deviceIds.forEach((id) => next.delete(id));
                return next;
            });
            starekContext.showSuccess?.(
                `Cleared Under Maintenance for ${deviceIds.length} device(s).`,
            );
        } catch (e) {
            starekContext.showError?.(
                e?.message || "Failed to clear Under Maintenance",
            );
        }
    };

    const doUnbind = async (ids) => {
        let ok = 0,
            fail = 0,
            firstErr = null;
        for (const id of ids) {
            try {
                await unbindDevice({ deviceId: id });
                ok++;
            } catch (e) {
                fail++;
                firstErr = firstErr || e;
            }
        }
        if (fail)
            starekContext.showError?.(
                `Unbound ${ok}/${ids.length}. ${firstErr?.message || "Some requests failed."
                }`,
            );
        else
            starekContext.showSuccess?.(
                `Unbound ${ok}/${ids.length} device(s).`,
            );
        await fetchDevices();
    };

    // TODO: Add loading state when response has not been received yet
    const doCommand = async (ids, command, parameters = {}) => {
        let ok = 0,
            fail = 0,
            firstErr = null;
        for (const id of ids) {
            try {
                await sendDeviceCommand({ deviceId: id, command, parameters });
                ok++;
            } catch (e) {
                fail++;
                firstErr = firstErr || e;
            }
        }
        if (fail)
            starekContext.showError?.(
                `Sent '${command}' to ${ok}/${ids.length}. ${firstErr?.message || "Some requests failed."
                }`,
            );
        else
            starekContext.showSuccess?.(
                `Sent '${command}' to ${ok}/${ids.length} device(s).`,
            );

        // Only refetch devices if the LiveView modal isn't open, to avoid
        // re-rendering underneath it
        if (!liveViewDevice) {
            await fetchDevices();
        }
    };

    const doReboot = async (ids) => {
        await doCommand(ids, "reboot", {
            reason: "portal-initiated",
            delay: 30000,
        });
        starekContext.showSuccess?.(
            `Reboot command sent to ${ids.length} device(s).`,
        );
    };
    const doFactoryReset = async (ids) => {
        await doCommand(ids, "reset_device", { reason: "portal-initiated" });
        starekContext.showSuccess?.(
            `Factory reset queued for ${ids.length} device(s).`,
        );
    };

    const doScheduleMaintenance = async (deviceIds, fromISO, toISO) => {
        const plantId = starekContext?.lastUsedPlant?.id ?? null;

        const user_Id = Number(starekContext?.user?.id);
        if (!user_Id) {
            throw new Error(
                "No logged-in user id found (created_by_id/updated_by_id required).",
            );
        }
        //TODO: Need to discuss about Scheduled Maintenance Name
        const rows = deviceIds.map((id) => ({
            name: `Scheduled Maintenance (${new Date(
                fromISO,
            ).toISOString()} → ${new Date(toISO).toISOString()})`,
            plant_id: plantId,
            schedule_type: TYPE_SCHEDULED,
            schedule_attributes: { from: fromISO, to: toISO },
            device_id: id,
            created_by_id: user_Id,
            updated_by_id: user_Id,
        }));

        try {
            await runCreateManyInChunks(rows);
            starekContext.showSuccess?.(
                `Maintenance scheduled for ${deviceIds.length} device(s).`,
            );
        } catch (e) {
            starekContext.showError?.(
                e.message || "Failed to schedule maintenance",
            );
        }
    };

    const customStyles = {
        table: { style: { overflow: "visible" } },
        tableWrapper: { style: { overflow: "visible" } },
        headRow: { style: { overflow: "visible" } },
        headCells: { style: { overflow: "visible" } },
        rows: { style: { overflow: "visible" } },
        cells: { style: { overflow: "visible", position: "relative" } },
    };

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return shadowDevices;
        return shadowDevices.filter((item) => {
            const fields = [
                item?.name,
                item?.id,
                item?.device?.model,
                item?.device?.firmware_version,
            ];
            return fields.some((v) =>
                (v ?? "").toString().toLowerCase().includes(q),
            );
        });
    }, [shadowDevices, filterText]);

    const columns = [
        {
            name: "Status",
            width: "120px",
            sortable: false,
            cell: (row) => {
                const heartbeatStatus = getHeartbeatStatus(
                    row.device.lastSeen.master,
                );
                const latestWifi = getLatestPoint(
                    signalHistory[row.id]?.["/virt/wifi_rssi"],
                );
                const latestBattery = getLatestPoint(
                    signalHistory[row.id]?.["/virt/pwr"],
                );
                const wifiPercent = latestWifi
                    ? (latestWifi.measure_value / 1023) * 100
                    : null;
                const batteryPercent = latestBattery
                    ? (latestBattery.measure_value / 1023) * 100
                    : null;
                const percentColorClass = (percent) =>
                    percent === null
                        ? "tw-text-gray-400"
                        : percent > 75
                            ? "tw-text-green-500"
                            : percent > 25
                                ? "tw-text-yellow-500"
                                : "tw-text-red-500";

                return (
                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                        <AiOutlineWifi
                            className={`tw-text-lg tw-cursor-pointer ${percentColorClass(wifiPercent)}`}
                            onClick={() =>
                                setSelectedSignalDialog({
                                    deviceId: row.id,
                                    signalKey: "/virt/wifi_rssi",
                                    title: "WiFi Signal — Last Hour",
                                    ipAddress:
                                        row.device.runtime.attributes.network
                                            ?.address,
                                })
                            }
                            size={18}
                            title={`WiFi: ${wifiPercent !== null ? wifiPercent.toFixed(0) + "%" : "No data"} · Connection: ${heartbeatStatus.label}`}
                        />
                        <IoLocationSharp
                            className="tw-text-blue-500 tw-cursor-pointer hover:tw-text-blue-700 tw-text-lg"
                            onClick={() =>
                                handleLocationClick(
                                    row.id,
                                    row.device.runtime.attributes,
                                )
                            }
                            size={18}
                            title="View Location"
                        />
                        <div
                            className="tw-flex tw-items-center tw-cursor-pointer"
                            title={`Battery: ${batteryPercent !== null ? batteryPercent.toFixed(0) + "%" : "No data"}`}
                            onClick={() =>
                                setSelectedSignalDialog({
                                    deviceId: row.id,
                                    signalKey: "/virt/pwr",
                                    title: "Battery Level — Last Hour",
                                })
                            }
                        >
                            <FaBatteryThreeQuarters
                                className={`tw-text-lg ${percentColorClass(batteryPercent)}`}
                                size={18}
                            />
                        </div>
                    </div>
                );
            },
            ignoreRowClick: true,
        },
        {
            name: "Image",
            width: "80px",
            sortable: false,
            cell: (row) => {
                const model = row.device?.model;
                if (!model) return null;
                return (
                    <img
                        src={`https://00117-starek.s3.us-east-1.amazonaws.com/branding/${model}.png`}
                        alt={model}
                        className="tw-w-14 tw-h-14 tw-object-contain"
                        onError={(e) => {
                            e.target.style.display = "none";
                        }}
                    />
                );
            },
            ignoreRowClick: true,
        },
        {
            name: "Device",
            selector: (row) => row.name || row.id,
            sortable: true,
            cell: (row) => {
                return (
                    <div className="tw-flex tw-flex-col tw-py-2">
                        <div className="tw-flex tw-items-center tw-mb-1">
                            <span
                                className="tw-font-bold tw-text-blue-600 hover:tw-underline tw-cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openEditSidebar(row);
                                }}
                                title="Edit device"
                            >
                                {row.name || row.id}
                            </span>
                        </div>
                        <div className="tw-text-sm tw-text-gray-500 tw-mb-2">
                            {row.device.model
                                ? `[${row.device.model}]`
                                : "Model"}
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <div className="tw-flex tw-items-center">
                                <span className="tw-text-xs tw-text-gray-400">
                                    FW:
                                </span>
                                <span className="tw-text-xs tw-text-gray-600 tw-ml-1">
                                    {row.device.firmware_version || ""}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            name: "Last Seen",
            sortable: true,
            cell: (row) => {
                if (row.device.controlledOffline) {
                    return (
                        <div className="tw-text-sm tw-text-gray-600">
                            Waiting to reconnect
                        </div>
                    );
                }
                const lastSeenTimestamp = row.device.lastSeen.master;
                const lastSeenMoment = moment(lastSeenTimestamp);
                const heartbeatStatus = getHeartbeatStatus(lastSeenTimestamp);

                return (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <div className="tw-text-sm tw-font-medium tw-text-gray-800">
                            {lastSeenMoment.format("MMM DD, YYYY")}
                        </div>
                        <div className="tw-text-xs tw-text-gray-500 tw-mb-1">
                            {lastSeenMoment.format("hh:mm A")}
                        </div>
                        <div className="tw-flex tw-items-center">
                            <span
                                className={classNames(
                                    "tw-px-2 tw-py-1 tw-rounded tw-text-xs",
                                    heartbeatStatus.style,
                                )}
                            >
                                {heartbeatStatus.label}
                            </span>
                        </div>
                    </div>
                );
            },
        },
        {
            name: "Job",
            cell: (row) => {
                if (row.device.controlledOffline) {
                    return (
                        <div className="tw-text-sm tw-text-gray-600">
                            {row.device.lastCommand?.command || "No command"}
                        </div>
                    );
                }
                return (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <div className="tw-text-sm tw-font-medium tw-text-gray-800">
                            {row.name || "No Assigned Jobs"}
                        </div>
                        {row.name && (
                            <div className="tw-flex tw-items-center">
                                <span className="tw-bg-blue-100 tw-text-blue-800 tw-text-xs tw-px-2 tw-py-1 tw-rounded">
                                    {(row.jobSchedules || []).reduce(
                                        (sum, js) =>
                                            sum + (js.completed_cycles || 0),
                                        0,
                                    )}{" "}
                                    Cycles
                                </span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            name: "Maintenance",
            cell: (row) => {
                if (row.device.controlledOffline) {
                    return (
                        <div className="tw-text-sm tw-text-gray-600">
                            {row.device.lastCommand?.parameters?.reason ||
                                "No reason"}
                        </div>
                    );
                }
                const status = getMaintenanceStatus(
                    row,
                    TYPE_ADHOC,
                    TYPE_SCHEDULED,
                );

                if (status.type === "adhoc") {
                    // The reason someone typed when marking it, not the generated
                    // record name. Older records carry none.
                    const reason =
                        status.data?.description ||
                        status.data?.schedule_attributes?.reason ||
                        null;
                    return (
                        <div className="tw-flex tw-flex-col">
                            <div className="tw-text-sm tw-font-medium tw-text-orange-600">
                                {status.display}
                            </div>
                            {reason && (
                                <div
                                    className="tw-text-xs tw-text-gray-700 tw-mt-1"
                                    title={reason}
                                >
                                    <i className="fa fa-comment tw-mr-1" />
                                    {reason}
                                </div>
                            )}
                        </div>
                    );
                }

                if (status.type === "scheduled") {
                    return (
                        <div className="tw-flex tw-flex-col">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <BsExclamationTriangleFill
                                    className="tw-text-yellow-500"
                                    size={16}
                                />
                                <div className="tw-text-sm tw-font-medium tw-text-yellow-700">
                                    {status.display}
                                </div>
                            </div>
                            <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                {status.humanized}
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="tw-flex tw-flex-col">
                        <div className="tw-text-sm tw-font-medium tw-text-green-600">
                            {status.display}
                        </div>
                    </div>
                );
            },
        },
        {
            name: "Actions",
            width: "80px",
            sortable: false,
            ignoreRowClick: true,
            cell: (row) => {
                if (row.device.controlledOffline) {
                    return null;
                }
                if (!canWrite && !canDestroy && !canReboot && !canUpdateFirmware) {
                    return null;
                }
                return (
                    <div className="tw-w-full tw-flex tw-justify-center">
                        <div className="tw-relative">
                            <button
                                type="button"
                                className={classNames(
                                    "tw-p-1 tw-rounded",
                                    hasSelection
                                        ? "tw-opacity-50 tw-cursor-not-allowed"
                                        : "hover:tw-bg-gray-100",
                                )}
                                aria-label="Actions"
                                title={
                                    hasSelection
                                        ? "Row actions are disabled when multiple rows are selected"
                                        : "Actions"
                                }
                                disabled={hasSelection}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    if (hasSelection) {
                                        e.preventDefault();
                                    }
                                }}
                                onClick={(e) => {
                                    if (hasSelection) return;
                                    e.stopPropagation();
                                    setMenuAnchorRect(
                                        e.currentTarget.getBoundingClientRect(),
                                    );
                                    setMenuOpenForId((prev) =>
                                        prev === row.id ? null : row.id,
                                    );
                                }}
                            >
                                <BsThreeDotsVertical
                                    className={classNames(
                                        hasSelection
                                            ? "tw-text-gray-300"
                                            : "tw-text-gray-600",
                                    )}
                                />
                            </button>

                            {menuOpenForId === row.id && (
                                <DeviceCardMenu
                                    anchorRect={menuAnchorRect}
                                    isUnderMaintenance={underMaintenance.has(
                                        row.id,
                                    )}
                                    onAddMaintenance={() => {
                                        setMenuOpenForId(null);
                                        openSchedule([row.id]);
                                    }}
                                    onUnderMaintenance={() => {
                                        setMenuOpenForId(null);
                                        const isClear = underMaintenance.has(
                                            row.id,
                                        );
                                        const deviceId = row.name || row.id;
                                        openConfirm({
                                            title: isClear
                                                ? "Return Device to Service?"
                                                : "Mark Device for Maintenance?",
                                            message: isClear
                                                ? DEVICE_MESSAGES.MAINTENANCE_CLEAR(
                                                    deviceId,
                                                )
                                                : DEVICE_MESSAGES.MAINTENANCE_MARK(
                                                    deviceId,
                                                ),
                                            confirmText: isClear
                                                ? "Return to Service"
                                                : "Mark Maintenance",
                                            cancelText: "Cancel",
                                            type: "warning",
                                            count: 1,
                                            requireComment: !isClear,
                                            onConfirm: async (details) => {
                                                if (isClear) {
                                                    await doClearUnderMaintenance(
                                                        [row.id],
                                                    );
                                                } else {
                                                    await doUnderMaintenance(
                                                        [row.id],
                                                        details,
                                                    );
                                                }
                                                closeConfirm();
                                            },
                                        });
                                    }}
                                    onUnbind={() => {
                                        setMenuOpenForId(null);
                                        const deviceId = row.name || row.id;
                                        openConfirm({
                                            title: "Unbind Device?",
                                            message:
                                                DEVICE_MESSAGES.UNBIND_SINGLE(
                                                    deviceId,
                                                ),
                                            type: "danger",
                                            confirmText: "Unbind",
                                            count: 1,
                                            onConfirm: async () => {
                                                await doUnbind([row.id]);
                                                closeConfirm();
                                            },
                                        });
                                    }}
                                    onReboot={() => {
                                        setMenuOpenForId(null);
                                        const deviceId = row.name || row.id;
                                        openConfirm({
                                            title: "Reboot confirmation?",
                                            message:
                                                DEVICE_MESSAGES.REBOOT_SINGLE(
                                                    deviceId,
                                                ),
                                            confirmText: "Reboot",
                                            count: 1,
                                            onConfirm: async () => {
                                                await doReboot([row.id]);
                                                closeConfirm();
                                            },
                                        });
                                    }}
                                    onUpdateFirmware={() => {
                                        setMenuOpenForId(null);
                                        openFirmwareModal(row);
                                    }}
                                    onFactoryReset={() => {
                                        setMenuOpenForId(null);
                                        const deviceId = row.name || row.id;
                                        openConfirm({
                                            title: "Factory reset confirmation?",
                                            message:
                                                DEVICE_MESSAGES.FACTORY_RESET_SINGLE(
                                                    deviceId,
                                                ),
                                            type: "danger",
                                            confirmText: "Reset",
                                            count: 1,
                                            onConfirm: async () => {
                                                await doFactoryReset([row.id]);
                                                closeConfirm();
                                            },
                                        });
                                    }}
                                    onClose={() => setMenuOpenForId(null)}
                                />
                            )}
                        </div>
                    </div>
                );
            },
        },
    ];
    //   const [visibleColumns, setVisibleColumns] = useState(columns);

    const handleLocationClick = (deviceId, attributes) => {
        setSelectedLocation({ deviceId, ...attributes });
    };
    const handleClosePopup = () => {
        setSelectedLocation(null);
    };
    const handleSaveLocation = async (deviceId, latitude, longitude) => {
        try {
            await Promise.all([
                deviceSetAttribute({
                    variables: { deviceId, name: "gps_lat", value: latitude },
                }),
                deviceSetAttribute({
                    variables: { deviceId, name: "gps_lon", value: longitude },
                }),
            ]);
            setSelectedLocation((prev) =>
                prev ? { ...prev, location: { latitude, longitude } } : prev,
            );
            starekContext.showSuccess?.("Location updated");
            refetch();
        } catch (e) {
            starekContext.showError?.(
                e?.message || "Failed to update location",
            );
        }
    };
    const openEditSidebar = (device, initialTab = "signals") => {
        setEditSidebarDevice(device);
        setEditSidebarInitialTab(initialTab);
        setEditSidebarOpen(true);
    };
    // LiveView's "no interpretations configured" banner - closes LiveView
    // and opens the same device straight on the Interpretation tab.
    const openInterpretationsFromLiveView = (device) => {
        setLiveViewDevice(null);
        openEditSidebar(device, "interpretation");
    };
    const handleEditSelected = () => {
        if (selectedRows.length !== 1) return;
        openEditSidebar(selectedRows[0]);
    };
    const lastUsedWorkspace = starekContext.lastUsedWorkspace;
    const initiateBinding = () => {
        if (starekContext.hasReachedLimit("devices")) {
            starekContext.showWarning(
                `Device limit reached. Your current plan allows up to ${starekContext.subscription?.limits?.devices} devices. Please upgrade to add more.`
            );
            return;
        }
        setOpenBindSidebar(true);
    };

    // Extended actions for bulk device operations
    const extendedActions = useMemo(() => {
        const actions = [];

        if (canWrite) {
            actions.push({
                label: "Add Scheduled Maintenance",
                icon: <AiOutlineClockCircle size={18} />,
                onClick: (rows) => {
                    openSchedule(rows.map((r) => r.id));
                },
                withDivider: true,
            });
            actions.push({
                label:
                    underMaintenance.size > 0
                        ? "Clear Maintenance"
                        : "Under Maintenance",
                icon: <BsStopwatch size={18} />,
                onClick: (rows) => {
                    const ids = rows.map((r) => r.id);
                    const already = ids.filter((id) =>
                        underMaintenance.has(id),
                    ).length;
                    const toCreate = ids.length - already;

                    openConfirm({
                        title: "Mark Devices for Maintenance?",
                        message:
                            already > 0
                                ? DEVICE_MESSAGES.MAINTENANCE_BULK_ALREADY(
                                    ids.length,
                                    toCreate,
                                    already,
                                )
                                : DEVICE_MESSAGES.MAINTENANCE_BULK_NEW(
                                    ids.length,
                                ),
                        confirmText: "Mark Maintenance",
                        cancelText: "Cancel",
                        type: "warning",
                        count: ids.length,
                        requireComment: true,
                        onConfirm: async (details) => {
                            await doUnderMaintenance(ids, details);
                            setClearSelectionTick((t) => !t);
                            closeConfirm();
                        },
                    });
                },
                withDivider: true,
            });
            actions.push({
                label: "Configure",
                icon: <BsGear size={18} />,
                onClick: (rows) => {
                    if (rows.length !== 1) {
                        starekContext.showError?.("Please select exactly one device to configure.");
                        return;
                    }
                    setConfigWizard({ open: true, device: rows[0] });
                },
                withDivider: true,
            });
        }

        if (canDestroy) {
            actions.push({
                label: "Unbind Device",
                icon: <BiUnlink size={18} />,
                onClick: (rows) => {
                    openConfirm({
                        title: "Unbind selected?",
                        message: DEVICE_MESSAGES.UNBIND_BULK(rows.length),
                        type: "danger",
                        confirmText: "Unbind",
                        count: rows.length,
                        onConfirm: async () => {
                            await doUnbind(rows.map((r) => r.id));
                            setClearSelectionTick((t) => !t);
                            closeConfirm();
                        },
                    });
                },
                withDivider: true,
            });
        }

        if (canUpdateFirmware) {
            actions.push({
                label: "Update firmware",
                icon: <MdSystemUpdateAlt size={18} />,
                onClick: (rows) => {
                    if (rows.length !== 1) {
                        starekContext.showError?.(
                            "Update firmware is allowed for one device at a time.",
                        );
                        return;
                    }
                    openFirmwareModal(rows[0]);
                },
                withDivider: true,
            });
        }

        if (canReboot) {
            actions.push({
                label: "Factory reset",
                icon: <TbRestore size={18} />,
                onClick: (rows) => {
                    openConfirm({
                        title: "Factory reset confirmation?",
                        message: DEVICE_MESSAGES.FACTORY_RESET_BULK(rows.length),
                        type: "danger",
                        confirmText: "Reset",
                        count: rows.length,
                        onConfirm: async () => {
                            await doFactoryReset(rows.map((d) => d.id));
                            closeConfirm();
                        },
                    });
                },
                withDivider: true,
            });
            actions.push({
                label: "Reboot device",
                icon: <BiPowerOff size={18} />,
                danger: true,
                onClick: (rows) => {
                    openConfirm({
                        title: "Reboot confirmation?",
                        message: DEVICE_MESSAGES.REBOOT_BULK(rows.length),
                        confirmText: "Reboot",
                        count: rows.length,
                        onConfirm: async () => {
                            await doReboot(rows.map((d) => d.id));
                            closeConfirm();
                        },
                    });
                },
            });
        }

        if (canWrite && selectedRows.length > 0) {
            actions.push({
                label: "Deactivate",
                icon: "fa-ban",
                danger: true,
                disabled: selectedRows.every((r) => !r.is_active),
                onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
                withDivider: true,
            });
            actions.push({
                label: "Restore",
                icon: "fa-undo",
                disabled: selectedRows.every((r) => r.is_active),
                onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
            });
        }

        return actions;
    }, [underMaintenance, canWrite, canDestroy, canReboot, canUpdateFirmware, selectedRows]);

    const handleToggleActive = async (devices, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = devices.map((d) => d.id);
        try {
            await updateManyShadowDevice({
                variables: {
                    data: makeActive ? restoreData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} device(s)` : `Deactivated ${ids.length} device(s)`);
            setSelectedRows([]);
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    if (deviceLoading) return <EntityLoadingState icon="fa-microchip" />;

    return (
        <div className="container-fluid">
            {shadowDevices && (
                <>
                    <EntityOperationBar
                        entity={{ singular: "Device", plural: "Devices" }}
                        allowSearch={true}
                        allowAdd={canWrite && !starekContext.hasReachedLimit("devices")}
                        allowDelete={false}
                        allowEdit={canWrite}
                        onEdit={handleEditSelected}
                        allowExtendedActions={extendedActions.length > 0}
                        extendedActions={extendedActions}
                        allowShowInactive={true}
                        showInactive={showInactive}
                        onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                        selectedRows={selectedRows}
                        searchPlaceholder="Search devices…"
                        addButtonText={
                            starekContext.hasReachedLimit("devices")
                                ? "Device Limit Reached"
                                : "Bind Device"
                        }
                        onSearch={(text) => setFilterText(text)}
                        onAdd={initiateBinding}
                    />

                    {deviceError ? (
                        <Alert variant="danger">
                            Couldn't load devices:{" "}
                            {deviceError.message || "Unknown error"}
                            <Button
                                variant="link"
                                className="tw-p-0 tw-ms-2 tw-align-baseline"
                                onClick={() => fetchDevices()}
                            >
                                Retry
                            </Button>
                        </Alert>
                    ) : !deviceData?.shadowDevices ||
                        deviceData.shadowDevices.length === 0 ? (
                        <EntityEmptyState
                            icon="fa-microchip"
                            entityName="Device"
                            messages={EMPTY_MESSAGES.devices}
                            isFiltered={false}
                            onAdd={initiateBinding}
                            addLabel="Bind a Device"
                        />
                    ) : (
                        <>
                            <DataTable
                                columns={columns}
                                data={filteredItems}
                                selectableRows
                                selectableRowsHighlight
                                onSelectedRowsChange={({ selectedRows }) => {
                                    setSelectedRows(selectedRows);
                                }}
                                clearSelectedRows={clearSelectionTick}
                                keyField="id"
                                pagination
                                highlightOnHover
                                striped
                                responsive
                                defaultSortField="name"
                                defaultSortAsc={true}
                                customStyles={customStyles}
                            />
                        </>
                    )}
                    <DeviceEditSidebar
                        isOpen={editSidebarOpen}
                        onClose={() => setEditSidebarOpen(false)}
                        device={editSidebarDeviceLive}
                        deviceProfiles={deviceProfilesForAssign}
                        canWrite={canWrite}
                        initialTab={editSidebarInitialTab}
                        zones={zonesForInstallations}
                        onSaveInstallations={async (deviceId, zoneIds) => {
                            await updateDeviceZones({
                                variables: {
                                    id: deviceId,
                                    zoneIds: zoneIds.map((id) => ({ id })),
                                },
                            });
                            await fetchDevices();
                        }}
                        onShowLiveView={(device) => setLiveViewDevice(device)}
                        onSaveSignals={async (deviceId, parameters) => {
                            await updateDeviceParameters({
                                variables: {
                                    where: { id: deviceId },
                                    data: { parameters },
                                },
                            });
                            // Explicitly awaited (not left to onCompleted's
                            // fire-and-forget) so the sidebar's own state can
                            // safely resync once this promise resolves.
                            await fetchDevices();
                            starekContext.showSuccess?.("Signals updated.");
                        }}
                        onAssignProfile={async (
                            deviceId,
                            deviceProfileId,
                            rebootNow,
                        ) => {
                            await assignDeviceProfile({
                                variables: {
                                    deviceId,
                                    deviceProfileId,
                                    rebootNow,
                                },
                            });
                            await fetchDevices();
                            starekContext.showSuccess?.(
                                "Profile assigned. Attribute changes are live now; signal role changes take effect the next time this device reconnects" +
                                (rebootNow ? " (rebooting now)." : "."),
                            );
                        }}
                        onUnassignProfile={async (deviceId, rebootNow) => {
                            await unassignDeviceProfile({
                                variables: { deviceId, rebootNow },
                            });
                            await fetchDevices();
                            starekContext.showSuccess?.("Profile unassigned.");
                        }}
                        onUpdateSignalType={async (
                            deviceId,
                            name,
                            type,
                            rebootNow,
                        ) => {
                            await updateDeviceSignalType({
                                variables: { deviceId, name, type, rebootNow },
                            });
                            await fetchDevices();
                        }}
                        onSaveLogicalSignal={async (deviceId, signals, rebootNow) => {
                            await updateDeviceSignals({
                                variables: { deviceId, signals, rebootNow },
                            });
                            await fetchDevices();
                            starekContext.showSuccess?.("Logical signal saved.");
                        }}
                        onSaveInterpretations={async (
                            deviceId,
                            interpretations,
                            rebootNow,
                        ) => {
                            await updateDeviceInterpretations({
                                variables: { deviceId, interpretations, rebootNow },
                            });
                            await fetchDevices();
                            starekContext.showSuccess?.("Interpretation saved.");
                        }}
                        onSaveAsProfile={async (deviceId, name, description) => {
                            const models = await fetchAvailableDeviceModels(starekContext);
                            const effectiveConfig = buildDeviceEffectiveConfiguration(
                                editSidebarDeviceLive,
                                models,
                                starekContext?.types?.device?.flags,
                            );
                            if (!effectiveConfig) {
                                throw new Error(
                                    "Could not resolve this device's model manifest",
                                );
                            }
                            const userId = Number(starekContext.user?.id);
                            await createDeviceProfile({
                                variables: {
                                    data: {
                                        name,
                                        description,
                                        configuration: effectiveConfig,
                                        plant: { connect: { id: starekContext.lastUsedPlant.id } },
                                        created_by: { connect: { id: userId } },
                                        updated_by: { connect: { id: userId } },
                                    },
                                },
                            });
                            starekContext.showSuccess?.(
                                `Saved this device's config as new Device Profile "${name}".`,
                            );
                        }}
                    />
                    {selectedLocation && (
                        <LocationPopup
                            locationData={selectedLocation}
                            onClose={handleClosePopup}
                            onSave={handleSaveLocation}
                            canEdit={canWrite}
                        />
                    )}
                    {selectedSignalDialog && (
                        <SignalHistoryPopup
                            title={selectedSignalDialog.title}
                            points={[
                                ...(signalHistory[
                                    selectedSignalDialog.deviceId
                                ]?.[selectedSignalDialog.signalKey] ?? []),
                            ].sort((a, b) => a.time - b.time)}
                            ipAddress={selectedSignalDialog.ipAddress}
                            onClose={() => setSelectedSignalDialog(null)}
                        />
                    )}
                    <Modal
                        show={!!liveViewDevice}
                        onHide={() => setLiveViewDevice(null)}
                        size="xl"
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>
                                LiveView — {liveViewDevice?.name || liveViewDevice?.id}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body
                            style={{ height: "70vh", display: "flex" }}
                        >
                            {liveViewDevice && (
                                <DeviceGraph
                                    device={liveViewDevice}
                                    isExpanded={true}
                                    sendDeviceCommand={sendDeviceCommand}
                                    deviceProfiles={deviceProfilesForAssign}
                                    onOpenInterpretations={
                                        openInterpretationsFromLiveView
                                    }
                                />
                            )}
                        </Modal.Body>
                    </Modal>
                    <ConfirmModal
                        open={confirmCfg.open}
                        type={confirmCfg.type}
                        title={confirmCfg.title}
                        message={confirmCfg.message}
                        count={confirmCfg.count}
                        subMessage={confirmCfg.subMessage}
                        confirmText={confirmCfg.confirmText}
                        confirmDisabled={
                            confirmCfg.requireComment &&
                            (!maintenanceComment.trim() ||
                                !maintenanceType ||
                                !maintenanceReasonId)
                        }
                        onConfirm={() =>
                            confirmCfg.onConfirm?.(
                                confirmCfg.requireComment
                                    ? {
                                        comment: maintenanceComment.trim(),
                                        maintenanceType:
                                            Number(maintenanceType) || null,
                                        downtimeReasonId:
                                            Number(maintenanceReasonId) ||
                                            null,
                                    }
                                    : {},
                            )
                        }
                        onClose={closeConfirm}
                    >
                        {confirmCfg.requireComment ? (
                            <div className="text-dark">
                                <div style={{ whiteSpace: "pre-wrap" }}>
                                    {confirmCfg.message}
                                </div>
                                <label
                                    className="form-label mt-3 mb-1"
                                    htmlFor="maintenance_type"
                                >
                                    Maintenance Type{" "}
                                    <span className="text-danger">*</span>
                                </label>
                                <select
                                    id="maintenance_type"
                                    className="form-select"
                                    autoFocus
                                    value={maintenanceType}
                                    onChange={(e) =>
                                        setMaintenanceType(e.target.value)
                                    }
                                >
                                    <option value="">Select a type…</option>
                                    {maintenanceTypeOptions.map((t) => (
                                        <option key={t.key} value={t.value}>
                                            {t.label}
                                        </option>
                                    ))}
                                </select>
                                <label
                                    className="form-label mt-3 mb-1"
                                    htmlFor="maintenance_downtime_reason"
                                >
                                    Downtime Reason{" "}
                                    <span className="text-danger">*</span>
                                </label>
                                <select
                                    id="maintenance_downtime_reason"
                                    className="form-select"
                                    value={maintenanceReasonId}
                                    disabled={downtimeReasons.length === 0}
                                    onChange={(e) =>
                                        setMaintenanceReasonId(e.target.value)
                                    }
                                >
                                    <option value="">
                                        {downtimeReasons.length === 0
                                            ? "No downtime reasons configured for this plant"
                                            : "Select a reason…"}
                                    </option>
                                    {downtimeReasons.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.name}
                                            {r.is_planned ? " (planned)" : ""}
                                        </option>
                                    ))}
                                </select>
                                <div className="form-text">
                                    Drives the Downtime Reason and maintenance
                                    reports, so pick the closest match.
                                </div>
                                <label
                                    className="form-label mt-3 mb-1"
                                    htmlFor="maintenance_reason"
                                >
                                    Why is it going under maintenance?{" "}
                                    <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    id="maintenance_reason"
                                    className="form-control"
                                    rows={3}
                                    maxLength={191}
                                    placeholder="e.g. spindle bearing noise, awaiting replacement part"
                                    value={maintenanceComment}
                                    onChange={(e) =>
                                        setMaintenanceComment(e.target.value)
                                    }
                                />
                                <div className="form-text">
                                    Recorded against the maintenance record, so
                                    the next person knows what was wrong.
                                </div>
                            </div>
                        ) : undefined}
                    </ConfirmModal>
                    <ScheduleMaintenanceModal
                        open={scheduleCfg.open}
                        onClose={closeSchedule}
                        onSubmit={async (startISO, endISO) => {
                            await doScheduleMaintenance(
                                scheduleCfg.targetIds,
                                startISO,
                                endISO,
                            );
                            closeSchedule();
                        }}
                    />
                    <BindDeviceSidebar
                        workspace={lastUsedWorkspace.name}
                        device={deviceData}
                        isOpen={openBindSidebar}
                        onClose={() => setOpenBindSidebar(false)}
                        onBindingComplete={fetchDevices}
                    />
                    {fwModal.open && fwModal.device && (
                        <UpdateFirmwareModal
                            open={fwModal.open}
                            onClose={() =>
                                setFwModal({ open: false, device: null })
                            }
                            model={fwModal.device?.device?.model}
                            currentVersion={
                                fwModal.device?.device?.firmware_version
                            }
                            currentCode={
                                fwModal.device?.device?.firmware_code || 1
                            }
                            onConfirm={(entry) => {
                                const deviceId = fwModal.device?.id;
                                const currentFw =
                                    fwModal.device?.device?.firmware_version ||
                                    "—";
                                setPendingFw(entry);
                                setFwModal({ open: false, device: null });
                                setTimeout(() => {
                                    openConfirm({
                                        title: "Update Firmware Confirmation?",
                                        message:
                                            "Are you sure you want to update the firmware for the selected device? Device will go offline and will reconnect after the firmware update has completed!",
                                        subMessage: `Device: ${deviceId}\nCurrent: ${currentFw}\nSelected: ${entry.firmware_version}`,

                                        confirmText: "Update",
                                        danger: false,
                                        onConfirm: async () => {
                                            try {
                                                await sendDeviceCommand({
                                                    deviceId,
                                                    command: "update_firmware",
                                                    parameters: {
                                                        firmware_code:
                                                            entry.firmware_code,
                                                        firmware_version:
                                                            entry.firmware_version,
                                                        url: entry.url,
                                                        release_date:
                                                            entry.release_date,
                                                        reason: "portal-initiated",
                                                    },
                                                });
                                                starekContext.showSuccess?.(
                                                    "Firmware update queued.",
                                                );
                                            } catch (e) {
                                                starekContext.showError?.(
                                                    e?.message ||
                                                    "Firmware update failed.",
                                                );
                                            } finally {
                                                setPendingFw(null);
                                                closeConfirm();
                                            }
                                        },
                                    });
                                }, 0);
                            }}
                        />
                    )}

                    {configWizard.open && configWizard.device && (
                        <Modal
                            show={configWizard.open}
                            onHide={() => setConfigWizard({ open: false, device: null })}
                            size="lg"
                            centered
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>Configure Device: {configWizard.device.name || configWizard.device.id}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <AddDeviceConfigWizard
                                    device={configWizard.device}
                                    onClose={() => setConfigWizard({ open: false, device: null })}
                                    onFinish={async (manifest) => {
                                        try {
                                            const deviceId = configWizard.device.id;

                                            if (manifest.config_source === "existing") {
                                                if (!manifest.selected_profile_id) {
                                                    throw new Error("No device profile selected");
                                                }
                                                await assignDeviceProfile({
                                                    variables: {
                                                        deviceId,
                                                        deviceProfileId: Number(manifest.selected_profile_id),
                                                    },
                                                });
                                                starekContext.showSuccess?.(
                                                    "Profile assigned. Attribute changes are live now; signal role changes take effect the next time this device reconnects."
                                                );
                                            } else {
                                                // Direct device configuration - flow 1.2: the values the
                                                // user provides here go straight onto this device
                                                // (overrides.signals for type, ShadowDevice.parameters for
                                                // cosmetics). No Device Profile is created or touched unless
                                                // the user explicitly opted in via "save as profile" below.
                                                await updateDeviceSignals({
                                                    variables: {
                                                        deviceId,
                                                        signals: flattenManifestSignalsToTypeArray(manifest.signals),
                                                    },
                                                });
                                                await updateDeviceParameters({
                                                    variables: {
                                                        where: { id: deviceId },
                                                        data: {
                                                            parameters: flattenManifestSignalsToCosmetics(manifest.signals),
                                                        },
                                                    },
                                                });

                                                if (manifest.save_as_profile && manifest.save_as_profile_name) {
                                                    const userId = Number(starekContext.user?.id);
                                                    await createDeviceProfile({
                                                        variables: {
                                                            data: {
                                                                name: manifest.save_as_profile_name,
                                                                description: manifest.save_as_profile_description,
                                                                configuration: {
                                                                    model: manifest.model,
                                                                    signals: manifest.signals,
                                                                },
                                                                plant: { connect: { id: starekContext.lastUsedPlant.id } },
                                                                created_by: { connect: { id: userId } },
                                                                updated_by: { connect: { id: userId } },
                                                            },
                                                        },
                                                    });
                                                    starekContext.showSuccess?.(
                                                        "Device configured and saved as a new Device Profile."
                                                    );
                                                } else {
                                                    starekContext.showSuccess?.(
                                                        "Device configured. Attribute changes are live now; signal role changes take effect the next time this device reconnects."
                                                    );
                                                }
                                            }

                                            await fetchDevices();
                                            setConfigWizard({ open: false, device: null });
                                        } catch (e) {
                                            starekContext.showError?.(e.message || "Failed to configure device");
                                        }
                                    }}
                                />
                            </Modal.Body>
                        </Modal>
                    )}
                </>
            )}
        </div>
    );
};
